import React, { useEffect, useMemo, useState } from "react";
import { notifyError } from "../../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../../apis";
import { endpoint } from "../../../utils/endpoints";
import LeftAngle from '../../../assets/image/left-angle.svg'
import RightAngle from '../../../assets/image/right-angle.svg'
import { formatDateTime as formatDateTimeDisplay } from "../../../utils/formatDateTime";
import { formatInr as formatRate } from "../../../utils/formatAmount";
import PageNumberInput from "../../../Components/Pagination/PageNumberInput";

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Handles ISO strings, unix ms/seconds, Mongo-style `{ $date }`, and dd/mm/yyyy text.
 * Returns a Date in local time, or null if unparseable.
 */
const parseToDate = (v) => {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;

  if (typeof v === "object" && v !== null && !(v instanceof Date)) {
    if (v.$numberLong != null) return parseToDate(String(v.$numberLong));
    const inner = v.$date ?? v.date ?? v.iso ?? v.value;
    if (inner != null) return parseToDate(inner);
  }

  if (typeof v === "number" && Number.isFinite(v)) {
    const ms = Math.abs(v) < 1e12 ? v * 1000 : v;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;

    if (/^-?\d+$/.test(s)) {
      const n = Number(s);
      if (Number.isFinite(n)) return parseToDate(n);
    }

    const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (isoDay) {
      const y = Number(isoDay[1]);
      const mo = Number(isoDay[2]);
      const da = Number(isoDay[3]);
      const d = new Date(y, mo - 1, da, 0, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(
      s
    );
    if (dmy) {
      let yy = Number(dmy[3]);
      if (dmy[3].length === 2) yy += yy >= 70 ? 1900 : 2000;
      const mo = Number(dmy[2]);
      const da = Number(dmy[1]);
      const hh = dmy[4] != null ? Number(dmy[4]) : 0;
      const mm = dmy[5] != null ? Number(dmy[5]) : 0;
      const ss = dmy[6] != null ? Number(dmy[6]) : 0;
      const d = new Date(yy, mo - 1, da, hh, mm, ss);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const fallback = new Date(v);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/** `effective_from` and similar — app 24h display via shared formatter */
const formatDateTime = (v) => {
  if (v == null || v === "") return "—";
  const d = parseToDate(v);
  if (!d) return typeof v === "string" ? v : "—";
  return formatDateTimeDisplay(d);
};

const extractHistoryArray = (raw) => {
  if (!raw || typeof raw !== "object") return [];
  const candidates = [
    raw.fuel_rate_history,
    raw.rate_history,
    raw.fuel_price_history,
    raw.history,
    raw.fuel_pump_rate_history,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
    if (c && Array.isArray(c.data)) return c.data;
  }
  return [];
};

const mapHistoryRows = (arr) =>
  (Array.isArray(arr) ? arr : []).map((item, index) => {
    const id = item?._id || item?.id || index;
    const ts =
      item?.effective_from ??
      item?.effectiveFrom ??
      item?.effective_from_date ??
      item?.date ??
      item?.created_at ??
      item?.updated_at ??
      item?.datetime ??
      item?.date_time;
    const rate =
      item?.fuel_rate ??
      item?.price ??
      item?.rate ??
      item?.new_rate;
    return {
      id,
      dateTime: formatDateTime(ts),
      fuelPrice: formatRate(rate),
    };
  });

const normalizePumpDetail = (d) => {
  if (!d || typeof d !== "object") return null;
  const raw =
    d?.fuel_pump_data ||
    d?.fuelPump_data ||
    d?.fuel_pump ||
    (Array.isArray(d) ? d[0] : null) ||
    d;

  if (!raw || typeof raw !== "object") return null;

  const miplLocation =
    raw?.mipl_location_id?.name ||
    raw?.mipl_location?.name ||
    "—";
  const fuelPumpName =
    raw?.fuel_pump_name ||
    raw?.name ||
    "—";
  const fuelRate = formatRate(raw?.fuel_rate);
  const status =
    raw?.status === 1 ? "Active" : raw?.status === 0 ? "Inactive" : "—";

  const addressSource =
    raw?.address ??
    d?.address ??
    raw?.location_address ??
    raw?.full_address;
  const address =
    addressSource != null && String(addressSource).trim() !== ""
      ? String(addressSource).trim()
      : "—";

  const fuelCompanyName =
    raw?.fuel_pump_company_id?.name ||
    raw?.company_id?.name ||
    raw?.fuel_company?.name ||
    "—";

  const historyFromPayload =
    raw === d
      ? extractHistoryArray(d)
      : [...extractHistoryArray(d), ...extractHistoryArray(raw)];
  const historyRows = mapHistoryRows(historyFromPayload);

  return {
    miplLocation,
    fuelPumpName,
    fuelRate,
    status,
    address,
    historyRows,
    fuelCompanyName,
  };
};

const ViewFuelPumpDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const fuelPumpId = (searchParams.get("fuel_pump_id") || "").trim();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setSubheading("View Fuel Pump Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!fuelPumpId) {
        setLoading(false);
        setDetail(null);
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewFuelPump}?fuel_pump_id=${encodeURIComponent(fuelPumpId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const raw = response?.data?.data;
          setDetail(normalizePumpDetail(raw));
        } else {
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel pump";
          notifyError(errMsg);
          setDetail(null);
        }
      } catch {
        notifyError("Failed to load fuel pump");
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fuelPumpId]);
 
  const drivers = detail?.historyRows ?? [];

  const filteredDrivers = useMemo(
    () =>
      drivers.filter((row) => {
        const q = search.toLowerCase();
        return (
          row.dateTime.toLowerCase().includes(q) ||
          row.fuelPrice.toLowerCase().includes(q)
        );
      }),
    [drivers, search]
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentDrivers = filteredDrivers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / itemsPerPage));

  if (!fuelPumpId && !loading) {
    return (
      <div className="mt-7">
        <p className="text-gray-600 mb-3">
          Missing fuel pump id. Open view from the fuel pump list.
        </p>
        <Link to="/fuel-pump-master" className="btn btn-primary px-4">
          Back
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="md:mt-7 mt-3">
        <div className="flex items-center mb-2">
            <h3 className="md:text-xl text-lg font-UbuntuMedium !my-0">Route Details</h3>
            <Link to="/fuel-pump-master" className="btn btn-primary ml-auto px-4">Back</Link>
        </div>

        {loading ? (
          <div className="rounded-lg px-4 py-8 bg-white mb-4 shadow-sm text-center text-gray-500">
            Loading...
          </div>
        ) : detail ? (
          <>
        <div className="rounded-lg md:px-4 px-3 py-2 bg-white mb-4 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 items-start max-[767px]:text-sm">
          <ul className="font-UbuntuMedium md:border-r md:border-gray-100 md:pr-8 space-y-0 min-w-0">
            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">MIPL Location</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right break-words">{detail.miplLocation}</div>
            </li>
            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">Fuel Pump Name</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right break-words">{detail.fuelPumpName}</div>
            </li>
            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">Fuel Company</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right break-words">{detail.fuelCompanyName}</div>
            </li>


            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">Fuel Rate</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right break-words">{detail.fuelRate}</div>
            </li>
          </ul>
          <ul className="font-UbuntuMedium space-y-0 min-w-0 md:pl-0">
            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">Address</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right break-words">
                {detail.address}
              </div>
            </li>
            <li className="flex items-start gap-3 py-1.5">
              <div className="label shrink-0 w-[9rem] text-gray-400">Status</div>
              <div className="font-UbuntuMedium min-w-0 flex-1 text-right">{detail.status}</div>
            </li>
          </ul>
        </div>
        <div className="common-table-wrapper m-0 shadow-sm">
            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            <th>Date & Time</th>
                            <th>Fuel Price</th>
                        </tr>
                    </thead>

                    <tbody>
                        {currentDrivers.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center py-4 text-gray-500">
                              No rate history available
                            </td>
                          </tr>
                        ) : (
                        currentDrivers.map((driver, index) => (
                            <tr key={driver.id}>
                                <td>{indexOfFirst + index + 1}</td>
                                <td>{driver.dateTime}</td>
                                <td>{driver.fuelPrice}</td>
                            </tr>
                        ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        <div className="flex gap-x-4 items-center md:mt-4 mt-3">

            <select
                className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                }}
                value={itemsPerPage}
            >
                <option value={10}>10</option>
                <option value={20}>20</option>
            </select>
            <div className="text-xs md:text-sm text-gray-500 ml-auto">
                {filteredDrivers.length} records found
            </div>
            <div className="flex items-center gap-2 md:pr-8">
                <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1 || filteredDrivers.length === 0}
                    className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                >
                    <img src={LeftAngle} alt="" className='invert grayscale brightness-100'/>
                </button>
                <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                <span className="text-sm text-gray-600 text-nowrap">
                    of {totalPages}
                </span>
                <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || filteredDrivers.length === 0}
                    className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                >
                    <img src={RightAngle} alt="" className='invert grayscale brightness-100'/>
                </button>

            </div>
        </div>
          </>
        ) : (
          <div className="rounded-lg px-4 py-6 bg-white mb-4 shadow-sm text-center text-gray-500">
            Could not load fuel pump details.
            <div className="mt-3">
              <Link to="/fuel-pump-master" className="btn btn-primary px-4">
                Back to list
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ViewFuelPumpDetails;
